import type {
  CustomFieldOption,
  FlowFieldOption,
  FlowQuizSummary,
  LocalizedString,
  PdfAdvisorNotePage,
} from "@repo/shared/types";
import type { PdfComponentType } from "../types";

export type FlowField = {
  id: string;
  name: string;
  nameI18n?: LocalizedString | null;
  type: string;
  // Option rows with stable ids (QL-201); rule values reference the id.
  options: FlowFieldOption[];
  scope?: "global" | "goal" | "portfolio";
};

export type ClientCustomField = {
  id: string;
  name: string;
  nameI18n?: LocalizedString | null;
  type: string;
  // Option rows with stable ids (QL-123); stored values reference the id.
  options?: CustomFieldOption[] | null;
  isActive: boolean;
  isStandard?: boolean;
};

// Base props that all component forms receive
export type ComponentFormProps<TConfig = Record<string, unknown>> = {
  config: TConfig;
  onChange: (config: TConfig) => void;
  flowFields: FlowField[];
  /** QL-13 — the `quiz` components on this template's flow. */
  quizzes?: FlowQuizSummary[];
  /** QL-414 — the flow pages that take advisor notes. */
  advisorNotePages?: PdfAdvisorNotePage[];
  clientCustomFields?: ClientCustomField[];
  error?: string | null;
  setError?: (error: string | null) => void;
};

// Component categories for grouping in the picker
export type ComponentCategory = "general" | "data" | "portfolio" | "costs" | "client" | "goal";

// Component type metadata (without translations) - translations should be defined locally
export type ComponentTypeMetadata = {
  type: PdfComponentType;
  category: ComponentCategory;
  hasForm: boolean;
  requiresPerGoal?: boolean;
  requiresXRay?: boolean;
  requiresRiskModel?: boolean;
  requiresPortfolioCollection?: boolean;
  /** QL-384 — only useful once the document has a Heading component. */
  requiresHeadings?: boolean;
  /** QL-384 — prints the flow's Financial situation component. */
  requiresFinancialSituation?: boolean;
};

// Full component type info (with translations) - for use in components that define translations locally
export type ComponentTypeInfo = ComponentTypeMetadata & {
  label: string;
  description: string;
  detailedDescription: string;
};

// Component type metadata only (no translations) - translations should be defined locally in files that use them
export const COMPONENT_TYPE_METADATA: ComponentTypeMetadata[] = [
  // General components
  { type: "text", category: "general", hasForm: true },
  { type: "heading", category: "general", hasForm: true },
  { type: "table_of_contents", category: "general", hasForm: true, requiresHeadings: true },
  { type: "image", category: "general", hasForm: true },
  { type: "advisor_image", category: "general", hasForm: true },
  { type: "page_break", category: "general", hasForm: false },
  { type: "spacer", category: "general", hasForm: true },

  // Data components
  { type: "table", category: "data", hasForm: true },

  // Portfolio components
  {
    type: "portfolio_display",
    category: "portfolio",
    hasForm: true,
    requiresPerGoal: true,
    requiresPortfolioCollection: true,
  },
  {
    type: "portfolio_xray",
    category: "portfolio",
    hasForm: true,
    requiresPerGoal: true,
    requiresXRay: true,
    requiresPortfolioCollection: true,
  },
  {
    type: "asset_allocation",
    category: "portfolio",
    hasForm: true,
    requiresPerGoal: true,
    requiresPortfolioCollection: true,
  },
  {
    // QL-315 — global or per-goal, never per-portfolio (see
    // GLOBAL_AND_GOAL_SCOPED_COMPONENTS), so no requiresPerGoal note.
    type: "asset_class_forecast",
    category: "portfolio",
    hasForm: true,
    requiresPortfolioCollection: true,
  },
  {
    type: "portfolio_forecast",
    category: "portfolio",
    hasForm: true,
    requiresPerGoal: true,
    requiresPortfolioCollection: true,
  },
  {
    type: "historical_return",
    category: "portfolio",
    hasForm: true,
    requiresPerGoal: true,
    requiresPortfolioCollection: true,
  },
  {
    type: "expected_value",
    category: "portfolio",
    hasForm: true,
    requiresPortfolioCollection: true,
  },
  {
    // QL-212 — no longer per-goal-only: a global section prints the block once
    // when the risk model captures all three inputs per client. Which section
    // scopes a given model allows is a dynamic answer, shown by the picker.
    type: "risk_assessment",
    category: "portfolio",
    hasForm: true,
    requiresRiskModel: true,
  },

  // Costs components
  { type: "cost_table", category: "costs", hasForm: true, requiresPortfolioCollection: true },
  { type: "cost_chart", category: "costs", hasForm: true, requiresPortfolioCollection: true },
  { type: "portfolio_costs", category: "costs", hasForm: true, requiresPortfolioCollection: true },
  { type: "product_costs", category: "costs", hasForm: true, requiresPortfolioCollection: true },

  // Client components
  { type: "client_classification", category: "client", hasForm: true },
  {
    type: "financial_situation",
    category: "client",
    hasForm: true,
    requiresFinancialSituation: true,
  },
  { type: "product_assessment", category: "client", hasForm: true },
  // QL-505 — standalone knowledge quiz results
  { type: "quiz_results", category: "client", hasForm: true },
  { type: "trapets_lookup", category: "client", hasForm: true },
  { type: "sumsub_verification", category: "client", hasForm: true },
  { type: "enin_lookup", category: "client", hasForm: true },
  // QP-184 — group members + related parties tables
  { type: "group_members", category: "client", hasForm: true },
  { type: "client_parties", category: "client", hasForm: true },
  // QP-224 — client preference answers
  { type: "client_preferences", category: "client", hasForm: true },

  // Goal components
  { type: "goal_info", category: "goal", hasForm: true, requiresPerGoal: true },
  // QL-281 — instance-wide goal list (any section scope)
  { type: "goal_list", category: "goal", hasForm: true },
  { type: "wealth_plan", category: "goal", hasForm: true },

  // QP-179 — sustainability
  { type: "sustainability_preferences", category: "client", hasForm: true },
  {
    type: "sustainability_status",
    category: "goal",
    hasForm: true,
    requiresPerGoal: true,
  },
  // QL-314 — volatility chart
  {
    type: "volatility_chart",
    category: "goal",
    hasForm: true,
    requiresPerGoal: true,
    requiresPortfolioCollection: true,
  },
  // QL-313 — equity share chart
  {
    type: "equity_share_chart",
    category: "goal",
    hasForm: true,
    requiresPerGoal: true,
    requiresPortfolioCollection: true,
  },
  // QL-375 group 10 — the mandate check and the follow-up comparison
  {
    type: "mandate",
    category: "goal",
    hasForm: true,
    requiresPerGoal: true,
    requiresPortfolioCollection: true,
  },
  {
    type: "mandate_comparison",
    category: "goal",
    hasForm: true,
    requiresPerGoal: true,
    requiresPortfolioCollection: true,
  },
  // QL-730 B2 — one portfolio's own mandate check (per-portfolio sections)
  {
    type: "mandate_portfolio",
    category: "portfolio",
    hasForm: true,
    requiresPerGoal: true,
    requiresPortfolioCollection: true,
  },
];

/**
 * QL-414 — the default for an absent `advisorNotePages` prop: one shared array,
 * so a field map memo keyed on the prop is not rebuilt on every render.
 */
export const NO_ADVISOR_NOTE_PAGES: PdfAdvisorNotePage[] = [];
